import * as React from "react";
import I18n from "i18n";
import * as _ from "underscore";
import pusherWrapper from "models/pusherWrapper";
import { ReaderNotificationConfigEmailForm } from "./ReaderNotificationConfigEmailForm";
import Notificator from "../../libraries/notificator";
import { apiUrl } from "../../config/backend";

const SenderDefaultValues = {
  email: function () {
    return I18n.t(
      "webapp.reader_admin.edit.notification_configuration.sender.default_email"
    );
  },
  name: function () {
    return I18n.t(
      "webapp.reader_admin.edit.notification_configuration.sender.default_name"
    );
  },
  warningContent: function () {
    return I18n.t(
      "webapp.reader_admin.edit.notification_configuration.sender.warning_content"
    );
  },
};

interface IProps {
  hidden: boolean;
  readerConfigurationId: number;
}

interface IState {
  replyToEmail: string;
  replyToName: string;
  fromEmail: string;
  fromName: string;
  fromEmailStatus: "none" | "new" | "checking" | "verified" | "active" | null;
  dnsText: string;
  version: string;
}

class ReaderNotificationConfigurationSenderFormView extends React.Component<
  IProps,
  IState
> {
  private pusherChannel: any;
  getEmailApiUrl = () =>
    apiUrl(`/api/v3/reader_configurations/${this.props.readerConfigurationId}/email_settings`);
  constructor(props: IProps) {
    super(props);
    this.state = {
      replyToEmail: "",
      replyToName: "",
      fromEmail: "",
      fromName: "",
      fromEmailStatus: null,
      dnsText: "",
      version: "",
    };
  }

  componentDidMount() {
    this.fetchEmailSettings();

    pusherWrapper.on("reader_email_settings_change", this.handlePusherEvent);
  }

  handlePusherEvent = (data: any) => {
    this.setState({
      version: data.version || "",
      fromName: data.from_name || "",
      fromEmail: data.from_email || "",
      replyToName: data.replyto_name || "",
      replyToEmail: data.replyto_email || "",
      fromEmailStatus: data.status || null,
      dnsText: data.dns_zone || "",
    });
  };

  fetchEmailSettings = async () => {
    try {
      const response = await fetch(this.getEmailApiUrl(), {
        credentials: "include",
        cache: "no-store",
      });
      const data = await response.json();
      this.setState({
        version: data.version || "",
        fromName: data.from_name || "",
        fromEmail: data.from_email || "",
        replyToName: data.replyto_name || "",
        replyToEmail: data.replyto_email || "",
        fromEmailStatus: data.status || null,
        dnsText: data.dns_zone || "",
      });
    } catch (error) {
      Notificator.showErrorNotification(I18n.t("webapp.reader_admin.edit.notification_configuration.email_settings.fetch_error"));
    }
  };

  async updateEmailSettings(operation?: string) {
    const { fromName, fromEmail, replyToName, replyToEmail, version } =
      this.state;

    if (!operation) {
      operation = version === "" ? "create" : "update";
    }

    try {
      const response = await fetch(this.getEmailApiUrl(), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          version,
          from_name: fromName,
          from_email: fromEmail,
          replyto_name: replyToName,
          replyto_email: replyToEmail,
          operation,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        this.setState({
          version: responseData.version || "",
          fromName: responseData.from_name || "",
          fromEmail: responseData.from_email || "",
          replyToName: responseData.replyto_name || "",
          replyToEmail: responseData.replyto_email || "",
          fromEmailStatus: responseData.status || null,
          dnsText: responseData.dns_zone || "",
        });
        if (responseData.status === "new") {
          Notificator.showNotification(
            I18n.t(
              "webapp.reader_admin.edit.notification_configuration.email_settings.verification_sent",
              { email: responseData.from_email }
            )
          );
        }
      }
    } catch (error) {
      Notificator.showErrorNotification(I18n.t("webapp.reader_admin.edit.notification_configuration.email_settings.update_error"));
    }
  }

  deleteEmailSettings = async () => {
    try {
      const response = await fetch(this.getEmailApiUrl(), {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        this.setState({
          replyToEmail: "",
          replyToName: "",
          fromEmail: "",
          fromName: "",
          fromEmailStatus: null,
          dnsText: "",
          version: "",
        });
      }
    } catch (error) {
      Notificator.showErrorNotification(I18n.t("webapp.reader_admin.edit.notification_configuration.email_settings.deletion_error"));
    }
  };

  handleBlur = () => {
    this.updateEmailSettings();
  };

  handleChange = (
    field: "fromEmail" | "fromName" | "replyToEmail" | "replyToName",
    value: string
  ) => {
    this.setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      this.updateEmailSettings();
    }
  };

  render() {
    if (this.props.hidden) return null;

    return (
      <div id="notification-sender-options">
        <div className="form-item mbm">
          <p className="text--grey">
            <strong>
              {I18n.t(
                "webapp.reader_admin.edit.notification_configuration.sender.warning_header"
              )}
            </strong>{" "}
            {SenderDefaultValues.warningContent()}
          </p>
        </div>
        <form>
          <ReaderNotificationConfigEmailForm
            senderNameLabel={I18n.t(
              "webapp.reader_admin.edit.notification_configuration.sender.from_sender_name_label"
            )}
            emailLabel={I18n.t(
              "webapp.reader_admin.edit.notification_configuration.sender.from_email_label"
            )}
            emailValue={this.state.fromEmail}
            senderValue={this.state.fromName}
            fromEmailStatus={this.state.fromEmailStatus}
            onEmailChange={(value) => this.handleChange("fromEmail", value)}
            onSenderChange={(value) => this.handleChange("fromName", value)}
            onBlur={() => this.handleBlur()}
            onKeyPress={(event) => this.handleKeyPress(event)}
            isFromEmail={true}
            onDnsStatusCheck={this.fetchEmailSettings}
            onDeleteEmailSettings={this.deleteEmailSettings}
            dnsText={this.state.dnsText}
          />
          <ReaderNotificationConfigEmailForm
            senderNameLabel={I18n.t(
              "webapp.reader_admin.edit.notification_configuration.sender.replyTo_sender_name_label"
            )}
            emailLabel={I18n.t(
              "webapp.reader_admin.edit.notification_configuration.sender.email_label"
            )}
            emailValue={this.state.replyToEmail}
            senderValue={this.state.replyToName}
            onEmailChange={(value) => this.handleChange("replyToEmail", value)}
            onSenderChange={(value) => this.handleChange("replyToName", value)}
            onBlur={() => this.handleBlur()}
            onKeyPress={(event) => this.handleKeyPress(event)}
          />
        </form>
      </div>
    );
  }
}

export { SenderDefaultValues, ReaderNotificationConfigurationSenderFormView };
